import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { filter } from 'rxjs';

import { RETURN_URL_KEY } from '@core/guards/auth-guard';

/** Routes that must never be stashed as a return URL or trigger a re-redirect. */
const PUBLIC_PATHS = ['/not-authorized', '/login', '/logout'];

export enum SessionLostReason {
  Expired = 'expired'
}

/**
 * The single, idempotent path for "the session is gone → send the user to the logged-out page".
 * Fed by two sources: the proactive OIDC `SilentRenewFailed` event (user sitting idle) and the
 * reactive 401 interceptor (a request found the token dead).
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private readonly router = inject(Router);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly events = inject(PublicEventsService);

  private redirecting = false;

  /** Subscribe to the library's silent-renew-failed event. Called once at app startup. */
  init(): void {
    this.events
      .registerForEvents()
      .pipe(filter((notification) => notification.type === EventTypes.SilentRenewFailed))
      .subscribe(() => this.handleSessionLost(SessionLostReason.Expired));
  }

  /** Idempotent: a near-simultaneous event + 401 cannot double-navigate. */
  handleSessionLost(reason: SessionLostReason): void {
    const currentUrl = this.router.url;
    if (this.redirecting || this.isPublicPath(currentUrl)) {
      return;
    }
    this.redirecting = true;

    sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
    this.oidcSecurityService.logoffLocal();
    this.router.navigate(['/not-authorized'], { queryParams: { reason } }).finally(() => (this.redirecting = false));
  }

  private isPublicPath(url: string): boolean {
    return PUBLIC_PATHS.some((path) => url.startsWith(path));
  }
}
